import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { apiService } from '@/services'
import { MILLISECONDS_PER_SECOND } from '@/services/constants'
import { ProcessingVideo, ProcessingVideoStatus, VideoPollingSource } from '@/services/videos/types'
import { AiVideoStatus, AiVideoStatusItem } from '@/services/ai-generated/types'
import { useProcessingVideosStorage } from '@/composables/useProcessingVideosStorage/useProcessingVideosStorage'
import { useProcessingPanel } from '@/composables/useProcessingPanel/useProcessingPanel'
import { isAiProcessingVideoId } from '@/composables/useProcessingPanelItem/useProcessingPanelItem'

// Module-level refs (singleton state - shared across all callers)
const uploadPollingInterval = ref<number | null>(null)
const aiPollingInterval = ref<number | null>(null)
const uploadVideos = ref<ProcessingVideo[]>([])
const aiVideos = ref<ProcessingVideo[]>([])

const totalVideos = ref<number>(0)
const completedVideos = ref<number>(0)
const successVideosToNotify = ref<string[]>([])
const trackedVideoIds = ref<string[]>([])
const failedVideos = ref<ProcessingVideo[]>([])
const completedVideoIds = ref<string[]>([])

const aiTrackedVideoIds = ref<string[]>([])
const aiCompletedVideoIds = ref<string[]>([])
const aiSuccessVideosToNotify = ref<string[]>([])
const aiFailedVideos = ref<ProcessingVideo[]>([])

const POLLING_INTERVAL = 5 * MILLISECONDS_PER_SECOND

const isValidUserId = (id: number | undefined | null): boolean => typeof id === 'number' && id > 0

export function stopAllPolling(): void {
  if (uploadPollingInterval.value) {
    clearInterval(uploadPollingInterval.value)
    uploadPollingInterval.value = null
  }
  if (aiPollingInterval.value) {
    clearInterval(aiPollingInterval.value)
    aiPollingInterval.value = null
  }
}

const AI_STATUS_MAP: Record<AiVideoStatus, ProcessingVideoStatus> = {
  [AiVideoStatus.Pending]: ProcessingVideoStatus.Pending,
  [AiVideoStatus.Processing]: ProcessingVideoStatus.Transcoding,
  [AiVideoStatus.Completed]: ProcessingVideoStatus.Active,
  [AiVideoStatus.Failed]: ProcessingVideoStatus.Failed
}

function toProcessingVideo(item: AiVideoStatusItem, userId: number): ProcessingVideo {
  return {
    video_id: `ai-${item.id}`,
    status: AI_STATUS_MAP[item.status],
    title: item.article_url,
    error: item.error_message,
    userId
  }
}

export function useVideoPolling() {
  const store = useStore()
  const userId = computed(() => store.state.user?.user?.id)
  const { updateItems, triggerRefresh } = useProcessingPanel()
  const { videos: storedVideos, clearUserVideos, getFailedVideosForUser, findVideoById, setVideos, addVideos, removeVideoById } = useProcessingVideosStorage()

  const hasProcessingVideos = computed<boolean>(() => totalVideos.value > completedVideos.value)

  const mergeAndUpdatePanel = () => {
    const allVideos = [...uploadVideos.value, ...aiVideos.value]
    if (allVideos.length === 0) {
      clearAllVideos()
      return
    }
    updateItems(allVideos)
  }

  const clearAllVideos = () => {
    if (isValidUserId(userId.value)) clearUserVideos(userId.value)
    updateItems([])
  }

  const clearNotifications = () => {
    totalVideos.value = 0
    completedVideos.value = 0
    successVideosToNotify.value = []
    trackedVideoIds.value = []
    failedVideos.value = []
    completedVideoIds.value = []
  }

  const clearAiNotifications = () => {
    aiTrackedVideoIds.value = []
    aiCompletedVideoIds.value = []
    aiSuccessVideosToNotify.value = []
    aiFailedVideos.value = []
  }

  const updateAiVideoStatus = (videos: ProcessingVideo[]): void => {
    if (!videos.length) return

    const currentVideoIds = videos.map((v) => v.video_id)

    currentVideoIds.forEach((id) => {
      if (!aiTrackedVideoIds.value.includes(id)) aiTrackedVideoIds.value.push(id)
    })

    const disappearedSuccessfulVideoIds = aiTrackedVideoIds.value.filter(
      (id) => !currentVideoIds.includes(id) && !aiCompletedVideoIds.value.includes(id)
    )

    if (disappearedSuccessfulVideoIds.length > 0) {
      aiSuccessVideosToNotify.value.push(...disappearedSuccessfulVideoIds)
      aiCompletedVideoIds.value.push(...disappearedSuccessfulVideoIds)
      triggerRefresh()
    }

    const newSuccessfulVideos = videos.filter(
      (video) => video.status === ProcessingVideoStatus.Active && !aiCompletedVideoIds.value.includes(video.video_id)
    )

    if (newSuccessfulVideos.length > 0) {
      const newIds = newSuccessfulVideos.map((v) => v.video_id)
      aiCompletedVideoIds.value.push(...newIds)
      aiSuccessVideosToNotify.value.push(...newIds)
      triggerRefresh()
    }

    const newFailedVideos = videos.filter(
      (video) => video.status === ProcessingVideoStatus.Failed && !aiCompletedVideoIds.value.includes(video.video_id)
    )

    if (newFailedVideos.length > 0) {
      const newIds = newFailedVideos.map((v) => v.video_id)
      aiCompletedVideoIds.value.push(...newIds)
      aiFailedVideos.value.push(...newFailedVideos)

      // Persist newly detected failed AI videos to localStorage
      newFailedVideos.forEach((video) => {
        if (!findVideoById(video.video_id)) {
          addVideos([video])
        }
      })
    }
  }

  const getFailedVideosNotInApiResponse = (apiVideos: ProcessingVideo[]): ProcessingVideo[] => {
    if (!isValidUserId(userId.value)) return []
    return getFailedVideosForUser(userId.value).filter((stored) => !apiVideos.find((v) => v.video_id === stored.video_id))
  }

  const getVideosToUpdate = (videos: ProcessingVideo[]): ProcessingVideo[] => {
    return videos.filter((video) => findVideoById(video.video_id))
  }

  const getStorageOnlyVideos = (apiVideos: ProcessingVideo[]): ProcessingVideo[] => {
    return storedVideos.value.filter(
      (stored) => !apiVideos.find((v) => v.video_id === stored.video_id) && stored.status !== ProcessingVideoStatus.Failed
    )
  }

  const mergeVideosWithStorage = (videos: ProcessingVideo[]): ProcessingVideo[] => {
    const storedById = new Map(storedVideos.value.map(v => [v.video_id, v]))

    const mergedVideos = videos.map((video) => {
      const storedVideo = storedById.get(video.video_id)
      return {
        ...video,
        title: storedVideo?.title ?? video.title,
        thumbnail_url: storedVideo?.thumbnail_url ?? video.thumbnail_url ?? null,
        error: storedVideo?.error ?? video.error,
        userId: storedVideo?.userId ?? video.userId
      }
    })

    if (storedVideos.value.length > 0) {
      const failedVideosToKeep = getFailedVideosNotInApiResponse(videos)
      const videosToUpdate = getVideosToUpdate(mergedVideos)
      const storageOnlyVideos = getStorageOnlyVideos(videos)

      const toStored = (v: ProcessingVideo) => ({
        video_id: v.video_id,
        title: v.title,
        thumbnail_url: v.thumbnail_url,
        status: v.status,
        error: v.error,
        userId: v.userId
      })

      setVideos([
        ...videosToUpdate.map(toStored),
        ...storageOnlyVideos,
        ...failedVideosToKeep
      ])
    }

    return mergedVideos
  }

  const updateVideoStatus = (videos: ProcessingVideo[]): void => {
    if (!videos.length) return

    const currentVideoIds = videos.map((v) => v.video_id)

    currentVideoIds.forEach((id) => {
      if (!trackedVideoIds.value.includes(id)) trackedVideoIds.value.push(id)
    })

    const disappearedSuccessfulVideoIds = trackedVideoIds.value.filter(
      (id) => !currentVideoIds.includes(id) && !completedVideoIds.value.includes(id)
    )

    if (disappearedSuccessfulVideoIds.length > 0) {
      successVideosToNotify.value.push(...disappearedSuccessfulVideoIds)
      completedVideoIds.value.push(...disappearedSuccessfulVideoIds)
      triggerRefresh()
    }

    totalVideos.value = trackedVideoIds.value.length

    const newSuccessfulVideos = videos.filter(
      (video) => video.status === ProcessingVideoStatus.Active && !completedVideoIds.value.includes(video.video_id)
    )

    if (newSuccessfulVideos.length > 0) {
      const newIds = newSuccessfulVideos.map((v) => v.video_id)
      completedVideoIds.value.push(...newIds)
      successVideosToNotify.value.push(...newIds)
      triggerRefresh()
    }

    const newFailedVideos = videos.filter(
      (video) => video.status === ProcessingVideoStatus.Failed && !completedVideoIds.value.includes(video.video_id)
    )

    if (newFailedVideos.length > 0) {
      const newIds = newFailedVideos.map((v) => v.video_id)
      completedVideoIds.value.push(...newIds)
      failedVideos.value.push(...newFailedVideos)
    }

    completedVideos.value = completedVideoIds.value.length

    const allSuccessfullyCompleted = videos.every((video) => video.status === ProcessingVideoStatus.Active)
    if (allSuccessfullyCompleted && completedVideos.value >= totalVideos.value) {
      clearUserVideos(userId.value)
    }
  }

  const fetchUploadVideos = async () => {
    if (!isValidUserId(userId.value)) return

    const videos = await apiService.videos.getUploadVideoStatus(userId.value.toString())
    if (!videos || !Array.isArray(videos)) return

    const failedVideosInStorage = getFailedVideosForUser(userId.value)

    if (videos.length === 0) {
      uploadVideos.value = failedVideosInStorage.length > 0
        ? failedVideosInStorage.map((stored) => ({
            video_id: stored.video_id,
            title: stored.title,
            thumbnail_url: stored.thumbnail_url,
            status: ProcessingVideoStatus.Failed,
            error: stored.error,
            userId: stored.userId
          }))
        : []
    } else {
      const mergedVideos = mergeVideosWithStorage(videos)
      uploadVideos.value = [...mergedVideos]
      failedVideosInStorage.forEach((failed) => {
        if (!uploadVideos.value.find((v) => v.video_id === failed.video_id)) {
          uploadVideos.value.push(failed)
        }
      })
    }

    updateVideoStatus(uploadVideos.value)

    const hasUploadPending = uploadVideos.value.some((v) => v.status !== ProcessingVideoStatus.Failed)
    if (!hasUploadPending) stopPolling(VideoPollingSource.VideoUpload)
  }

  const fetchAiVideos = async () => {
    if (!isValidUserId(userId.value)) return

    const { items } = await apiService.aiGenerated.getAiVideoStatus()

    const apiVideos = items.map((item) => toProcessingVideo(item, userId.value))

    const storedFailedAiVideos = getFailedVideosForUser(userId.value)
      .filter((stored) => isAiProcessingVideoId(stored.video_id))
      .filter((stored) => !apiVideos.find((v) => v.video_id === stored.video_id))
      .map((stored) => ({
        video_id: stored.video_id,
        title: stored.title,
        thumbnail_url: stored.thumbnail_url,
        status: ProcessingVideoStatus.Failed,
        error: stored.error,
        userId: stored.userId
      }))

    aiVideos.value = [...apiVideos, ...storedFailedAiVideos]

    updateAiVideoStatus(aiVideos.value)

    items
      .filter((item) => item.status === AiVideoStatus.Completed)
      .forEach((item) => removeVideoById(`ai-${item.id}`))

    const hasAiPending = items.some(
      (item) => item.status === AiVideoStatus.Pending || item.status === AiVideoStatus.Processing
    )
    if (!hasAiPending) stopPolling(VideoPollingSource.AiGenerateVideo)
  }

  const fetchData = async (source: VideoPollingSource) => {
    try {
      if (source === VideoPollingSource.VideoUpload) await fetchUploadVideos()
      if (source === VideoPollingSource.AiGenerateVideo) await fetchAiVideos()
      mergeAndUpdatePanel()
    } catch (error) {
      console.error(`Polling error (${source}):`, error)
    }
  }

  const startPolling = async (pollingSource: VideoPollingSource) => {
    const interval = pollingSource === VideoPollingSource.VideoUpload ? uploadPollingInterval : aiPollingInterval
    if (interval.value) return
    interval.value = window.setInterval(() => fetchData(pollingSource), POLLING_INTERVAL)
    await fetchData(pollingSource)
  }

  const stopPolling = (pollingSource: VideoPollingSource) => {
    const interval = pollingSource === VideoPollingSource.VideoUpload ? uploadPollingInterval : aiPollingInterval
    if (interval.value) {
      clearInterval(interval.value)
      interval.value = null
    }
  }

  return {
    startPolling,
    stopPolling,
    fetchData,
    totalVideos,
    completedVideos,
    successVideosToNotify,
    failedVideos,
    hasProcessingVideos,
    clearNotifications,
    aiSuccessVideosToNotify,
    aiFailedVideos,
    clearAiNotifications
  }
}
